import { useEffect, useState } from "react";

/**
 * The one shape every chart in the kit draws from (EP13-S04), so the chart and
 * its table fallback can never disagree: both are built from the same rows.
 *
 * Colours are token references only (DA-13-01). `check-tokens.mjs` fails on a
 * hex literal anywhere under `ui/charts`, so dark mode always has something to
 * swap.
 */

export type ChartRow = Record<string, string | number | null>;

export interface ChartSeries {
	/** The row field this series reads. */
	key: string;
	label: string;
	/**
	 * Its categorical colour, 1 to 8. Fixed per meaning ("delivered" is always
	 * the same colour), so a series keeps its colour from chart to chart.
	 * Defaults to its position in the list.
	 */
	color?: number;
}

export interface ChartData {
	/** The category axis: a day, a stage, a bucket, a store. */
	x: { key: string; label: string; format?: (value: string) => string };
	series: ChartSeries[];
	rows: ChartRow[];
	/** One value as it reads in a label, a tooltip and the table. */
	format: (value: number) => string;
}

/** The categorical token for a series: its own colour, or its position's. */
export function seriesColor(series: ChartSeries, index: number): string {
	return `var(--viz-cat-${(((series.color ?? index + 1) - 1) % 8) + 1})`;
}

/**
 * Dash patterns for the second and later lines of a chart, so two series
 * never differ by colour alone (`13/01` §7).
 */
export const DASHES = ["", "6 3", "2 3", "8 3 2 3"] as const;

export function dashFor(index: number): string {
	return DASHES[index % DASHES.length] ?? "";
}

/**
 * Which step of the five-step sequential ramp a value falls on, between the
 * smallest and largest of its matrix. A matrix of one value is step 3, the
 * middle, because it is neither high nor low.
 */
export function seqStep(value: number, min: number, max: number): 1 | 2 | 3 | 4 | 5 {
	if (max <= min) {
		return 3;
	}
	const step = Math.floor(((value - min) / (max - min)) * 5) + 1;
	return Math.min(5, Math.max(1, step)) as 1 | 2 | 3 | 4 | 5;
}

/** A cell's ground and the label ink that clears 4.5:1 on it (check-contrast). */
export function seqColors(step: number): { background: string; color: string } {
	return {
		background: `var(--viz-seq-${step})`,
		color: step <= 3 ? "var(--fg)" : "var(--viz-seq-ink)",
	};
}

/** The x value as it reads: through the axis formatter when there is one. */
export function xText(data: ChartData, row: ChartRow): string {
	const raw = row[data.x.key];
	const text = raw === null || raw === undefined ? "" : String(raw);
	return data.x.format === undefined ? text : data.x.format(text);
}

/** A cell's figure, or a dash for a gap. */
export function valueText(data: ChartData, value: string | number | null | undefined): string {
	return typeof value === "number" ? data.format(value) : "—";
}

/** Whether a media query matches, following changes. False where there is no `matchMedia`. */
export function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState(() => {
		try {
			return window.matchMedia?.(query).matches === true;
		} catch {
			return false;
		}
	});
	useEffect(() => {
		const list = window.matchMedia?.(query);
		if (list === undefined) {
			return;
		}
		setMatches(list.matches);
		const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
		list.addEventListener("change", onChange);
		return () => list.removeEventListener("change", onChange);
	}, [query]);
	return matches;
}

/** At or under 360 px a chart card shows only its table (`13/01` §7). */
export const NARROW_QUERY = "(max-width: 360px)";

export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Whether the document runs right to left, which is when time does too. */
export function isRtl(): boolean {
	return typeof document !== "undefined" && document.documentElement.dir === "rtl";
}

/**
 * The cartesian axes for a direction: in Arabic the category axis runs right
 * to left and the value axis stands on the right, the reading start.
 */
export function cartesianAxes(rtl: boolean): {
	reversed: boolean;
	valueSide: "left" | "right";
	secondarySide: "left" | "right";
} {
	return rtl
		? { reversed: true, valueSide: "right", secondarySide: "left" }
		: { reversed: false, valueSide: "left", secondarySide: "right" };
}
